import React, { useState } from 'react';
import {
    IonCard,
    IonCardHeader,
    IonToolbar,
    IonButtons,
    IonButton,
    IonIcon,
    IonTitle,
    IonGrid,
    IonRow,
    IonCol,
    IonInput,
    IonSpinner,
} from '@ionic/react';
import { arrowBack, eye, eyeOff, lockClosed } from 'ionicons/icons';
import styles from '../../pages/login/LoginReg.module.scss';
import { useTranslation } from 'react-i18next';

interface PasswordCardProps {
    formData: {
        password: string;
    };
    uiState: {
        loggingIn: boolean;
        showPassword: boolean;
        showResendVerification: boolean;
    };
    isDisabled: boolean;
    hasValidEmail: boolean;
    onGoBack: () => void;
    onLogin: () => void;
    onPasswordReset: () => void;
    onResendVerification: () => void;
    onInputChange: (field: string, value: string) => void;
}

export const PasswordCard: React.FC<PasswordCardProps> = ({
    formData,
    uiState,
    isDisabled,
    hasValidEmail,
    onGoBack,
    onLogin,
    onPasswordReset,
    onResendVerification,
    onInputChange,
}) => {
  const { t } = useTranslation();
    const [showPassword, setShowPassword] = useState(false);

    return (
    <IonCard className={`${styles.loginCard} ion-text-center`}>
        <IonCardHeader className={`${styles.cardHeader} ion-text-center`}>
            <IonToolbar>
                <IonButtons slot="start">
                    <IonButton
                        fill="clear"
                        shape='round'
                        onClick={onGoBack}
                        disabled={isDisabled}
                        className={styles.backButton}
                    >
                        <IonIcon icon={arrowBack} />
                    </IonButton>
                </IonButtons>
                <IonTitle className={styles.cardTitle}>{t('components.AuthComponents.PasswordCard.enterPassword')}</IonTitle>
            </IonToolbar>
        </IonCardHeader>
        <IonGrid>
            <IonRow>
                <IonCol>
                    <form onSubmit={(e) => { e.preventDefault(); onLogin(); }}>
                        <IonInput
                            id="login-password"
                            className={`${styles.customInput} ion-margin-top`}
                            labelPlacement="stacked"
                            label={t('components.AuthComponents.PasswordCard.password')}
                            type={showPassword ? 'text' : 'password'}
                            placeholder={t('components.AuthComponents.PasswordCard.enterYourPassword')}
                            value={formData.password}
                            onIonInput={(e) => onInputChange('password', e.detail.value!)}
                            disabled={isDisabled}
                            required
                        >
                            <IonIcon icon={lockClosed} slot="start" />
                            <IonButton
                                slot="end"
                                fill="clear"
                                onClick={() => setShowPassword(p => !p)}
                                disabled={isDisabled}
                                className={styles.passwordToggle}
                            >
                                <IonIcon icon={showPassword ? eye : eyeOff} />
                            </IonButton>
                        </IonInput>
                        <div className={styles.rememberForgotContainer}>
                            <IonButton
                                fill="clear"
                                size="small"
                                onClick={onPasswordReset}
                                disabled={isDisabled}
                                className={styles.forgotPasswordBtn}
                            >{t('components.AuthComponents.PasswordCard.forgotPassword')}
                            </IonButton>
                        </div>
                        <div className={styles.centerCol}>
                            <IonButton
                                expand="block"
                                type="submit"
                                disabled={isDisabled}
                                className={`${styles.customButton} ${styles.mainButton}`}
                            >
                                {uiState.loggingIn ? (
                                    <>{t('components.AuthComponents.PasswordCard.loggingIn')}
                                        <IonSpinner name="dots" slot="end" />
                                    </>
                                ) : t('components.AuthComponents.PasswordCard.login')}
                            </IonButton>
                            {uiState.showResendVerification && hasValidEmail && (
                                <IonButton
                                    expand="block"
                                    fill="outline"
                                    onClick={onResendVerification}
                                    disabled={isDisabled}
                                    className={`${styles.customButton} ion-margin-top`}
                                >
                                    {uiState.loggingIn ? <IonSpinner name="dots" /> : t('components.AuthComponents.PasswordCard.resendVerificationEmail')}
                                </IonButton>
                            )}
                        </div>
                    </form>
                </IonCol>
            </IonRow>
        </IonGrid>
    </IonCard>
    );
};
